/**
 * Export Table Data to Excel
 * Handles Excel generation with conditional formatting for disaster reports
 * Matches UI Team format: No header rows, direct headers in Row 1, data from Row 2
 */

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { formatDate, transformDataForExcel, transformMISDataForExcel, formatDateForMISExcel, getTimestamp } from "./ExcelUtils";

/**
 * Apply conditional formatting for Cold Wave Report
 * RED: No reports submitted (report_submitted === 0)
 * GREEN: All reports submitted (report_not_submitted === 0)
 * YELLOW: Partial submission
 */
const applyColdWaveFormatting = (worksheet, columns) => {
  // Find column indices for Report Submitted and Report Not Submitted
  let reportSubmittedColIndex = -1;
  let reportNotSubmittedColIndex = -1;

  columns.forEach((col, index) => {
    if (col.label === "Report Submitted") {
      reportSubmittedColIndex = index + 1; // ExcelJS uses 1-based indexing
    }
    if (col.label === "Report Not Submitted") {
      reportNotSubmittedColIndex = index + 1; // ExcelJS uses 1-based indexing
    }
  });

  // Apply formatting to data rows (skip header row 1)
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return; // Skip header row

    const reportSubmittedCell = row.getCell(reportSubmittedColIndex);
    const reportNotSubmittedCell = row.getCell(reportNotSubmittedColIndex);

    const reportSubmittedValue = reportSubmittedCell.value;
    const reportNotSubmittedValue = reportNotSubmittedCell.value;

    const shouldHighlightRed = reportSubmittedValue === 0;
    const shouldHighlightGreen = reportNotSubmittedValue === 0;

    // Apply fill color to entire row
    row.eachCell({ includeEmpty: true }, (cell) => {
      if (shouldHighlightRed) {
        // RED: No reports submitted
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFF0000" },
        };
        // ❌ REMOVED: white text color - keep black for readability
      } else if (shouldHighlightGreen) {
        // GREEN: All reports submitted
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF00FF00" },
        };
      } else {
        // YELLOW: Partial submission
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFFFF00" },
        };
      }
    });
  });
};

/**
 * Main Excel export function
 * Format matches UI Team: Headers in Row 1, Data starts Row 2
 * @param {Object} reportData - Contains data, metadata, and configuration
 * @param {Array} reportData.data - Report data array
 * @param {Array} reportData.columnOrder - Column order from backend
 * @param {string} reportData.toolName - Tool name (e.g., 'coldwave_daily_report')
 * @param {string} reportData.date - Report date (YYYY-MM-DD)
 * @param {string} reportData.filename - Base filename
 */
export const generateExcelReport = async (reportData) => {
  const { data, columnOrder, toolName, date, filename } = reportData;

  if (!data || data.length === 0) {
    alert("No data available to export.");
    return;
  }

  try {
    // Transform data
    const { columns, transformedData } = transformDataForExcel(data, columnOrder);

    // Create workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sheet 1");

    // ===== NEW FORMAT: NO metadata rows, direct headers =====
    
    // Row 1: Column Headers ONLY (no report name, date info)
    worksheet.addRow(columns.map((col) => col.label));

    // Row 2+: Data rows
    transformedData.forEach((row) => {
      const rowData = columns.map((col) => row[col.label]);
      worksheet.addRow(rowData);
    });

    // === FORMATTING ===

        // Format Row 1 (Headers) - Apply alignment based on column type
    worksheet.getRow(1).eachCell((cell, colNumber) => {
      // Name column (column 1) = left-aligned
      // Numeric columns (2, 3, 4) = right-aligned
      const alignment = colNumber === 1 ? "left" : "right";
      cell.alignment = { horizontal: alignment, vertical: "middle" };
      // NO font styling - keep default (normal, not bold)
      // NO fill color - keep default white background
    });

    // Format data rows (Row 2+) - Apply alignment based on column type
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 1) {
        row.eachCell((cell, colNumber) => {
          // Name column (column 1) = left-aligned
          // Numeric columns (2, 3, 4) = right-aligned
          const alignment = colNumber === 1 ? "left" : "right";
          cell.alignment = { horizontal: alignment, vertical: "middle" };
        });
      }
    });
    // === CONDITIONAL FORMATTING ===
    if (toolName === "coldwave_daily_report_status") {
      applyColdWaveFormatting(worksheet, columns);
    }
    // Add more report-specific formatting as needed
    // else if (toolName === "fire_daily_report") {
    //   applyFireReportFormatting(worksheet, columns);
    // }

    // === AUTO-ADJUST COLUMN WIDTHS ===
    columns.forEach((col, index) => {
      const columnLetter = String.fromCharCode(65 + index); // A, B, C, etc.
      let maxLength = col.label.length;

      transformedData.forEach((row) => {
        const cellValue = String(row[col.label] || "");
        maxLength = Math.max(maxLength, cellValue.length);
      });

      worksheet.getColumn(columnLetter).width = Math.min(maxLength + 2, 40);
    });

// === GENERATE AND DOWNLOAD FILE ===
const buffer = await workbook.xlsx.writeBuffer();
const blob = new Blob([buffer], {
  type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
});

const timestamp = getTimestamp();

// ✅ FIX: Normalize filename to match web UI
let baseFilename;
if (toolName === "coldwave_daily_report_status") {
  baseFilename = "DailyColdWaveReportStatus";
} else {
  baseFilename = filename || "Cold_Wave_Report";
}

const finalFilename = `${baseFilename}_${timestamp}.xlsx`;

saveAs(blob, finalFilename);
console.log(`✅ Excel file generated: ${finalFilename}`);

  } catch (error) {
    console.error("Excel generation error:", error);
    throw error;
  }
};
/**
 * ✅ NEW: MIS Report Excel Export (matches reference format exactly)
 */


/**
 * ✅ UPDATED: Unified MIS Report Excel Generator - Handles all 4 types
 */
export const generateMISExcelReport = async (reportData, reportType) => {
  const { data, fromDate, toDate, filename, selectedView, displayData } = reportData;
  const type = reportType || selectedView || 'dist_date';
  
  console.log("🔍 MIS Report Excel Generation:", { type, fromDate, toDate });
  console.log("📦 Report Data Structure:", { hasData: !!data, hasDisplayData: !!displayData });

  if (!data || typeof data !== 'object') {
    alert("No data available to export.");
    return;
  }

  try {
    // Import new transformers
    const { transformMISDistrictData, transformMISStateData, transformMISStateDatewiseData } = await import('./ExcelUtils');
    
    // ✅ NEW: Merge cumulative data for state report BEFORE transformation
    let processedData = data;
    
    if (type === 'state' && displayData?.cumulative_data?.dr_cold_wave_cum) {
      console.log("⏭️ Skipping frontend merge - backend already merged cumulative fields");
    processedData = data;
    }
    
    // Transform data based on report type
    let transformedResult;
    let reportName;

    switch (type) {
      case 'dist_date':
        transformedResult = transformMISDataForExcel(processedData);
        reportName = 'DIST_DATE_COLD_WAVE_REPORT';
        break;
      
      case 'dist':
        transformedResult = transformMISDistrictData(processedData);
        reportName = 'Dist_Cold_Wave_Report';
        break;
      
      case 'state':
        transformedResult = transformMISStateData(processedData);
        reportName = 'STATE_COLD_WAVE_REPORT';
        break;
      
      case 'state_date':
        transformedResult = transformMISStateDatewiseData(processedData);
        reportName = 'STATE_DATE_COLD_WAVE_REPORT';
        break;
      
      default:
        transformedResult = transformMISDataForExcel(processedData);
        reportName = 'COLD_WAVE_REPORT';
    }

    const { columns, transformedData } = transformedResult;

    if (transformedData.length === 0) {
      alert("No data available to export.");
      return;
    }

    console.log(`✅ Transformed ${transformedData.length} rows`);

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sheet 1");

    // Row 1-3: Metadata - Bold, Merged, Left-aligned
    const metadataRow1 = worksheet.addRow([`Report Name: ${reportName}`]);
    metadataRow1.getCell(1).font = { bold: true };
    metadataRow1.getCell(1).alignment = { horizontal: "left", vertical: "middle" };
    worksheet.mergeCells(`A1:${String.fromCharCode(64 + columns.length)}1`);

    const metadataRow2 = worksheet.addRow([`From Date: ${fromDate || 'N/A'}`]);
    metadataRow2.getCell(1).font = { bold: true };
    metadataRow2.getCell(1).alignment = { horizontal: "left", vertical: "middle" };
    worksheet.mergeCells(`A2:${String.fromCharCode(64 + columns.length)}2`);

    const metadataRow3 = worksheet.addRow([`To Date: ${toDate || 'N/A'}`]);
    metadataRow3.getCell(1).font = { bold: true };
    metadataRow3.getCell(1).alignment = { horizontal: "left", vertical: "middle" };
    worksheet.mergeCells(`A3:${String.fromCharCode(64 + columns.length)}3`);

    // Row 4: Headers (Bold, left-aligned)
    const headerRow = worksheet.addRow(columns.map((col) => col.label));
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
    });

    // Row 5+: Data rows (left-aligned)
    transformedData.forEach((row) => {
      const rowData = columns.map((col) => {
        const value = row[col.label];
        return value !== undefined && value !== null ? value : "";
      });
      const dataRow = worksheet.addRow(rowData);
      dataRow.eachCell((cell) => {
        cell.alignment = { horizontal: "left", vertical: "middle" };
      });
    });

    // ✅ UPDATED: TOTAL row calculation for dist_date and state_date only
    // (state report already has TOTAL from transformMISStateData)
    // ✅ FIXED: Only add TOTAL for state_date (others already have TOTAL from transformers)
    // - dist_date: transformMISDataForExcel adds TOTAL
    // - dist: transformMISDistrictData adds TOTAL  
    // - state: transformMISStateData adds TOTAL
    // - state_date: transformMISStateDatewiseData does NOT add TOTAL (needs manual)
    if (type === 'state_date') {  // ← ONLY state_date needs manual TOTAL
      const totalRowIndex = transformedData.length + 5;
      const totalRow = worksheet.getRow(totalRowIndex);
      totalRow.getCell(1).value = 'TOTAL';
      totalRow.getCell(2).value = 'NA';
      // No Block Name column in state_date
      
      // Calculate sums for numeric columns
      const startCol = 3; // Skip District Name (1), Date (2)
      
      for (let colIndex = startCol; colIndex <= columns.length; colIndex++) {
        const colLabel = columns[colIndex - 1].label;
        const isCumulativeColumn = colLabel.includes("(Cum)");
        
        let sum = 0;
        let allDashes = true;
        
        for (let rowIndex = 5; rowIndex < totalRowIndex; rowIndex++) {
          const cellValue = worksheet.getRow(rowIndex).getCell(colIndex).value;
          if (cellValue === "-") {
            continue;
          } else {
            allDashes = false;
          }
          
          const numValue = parseFloat(cellValue);
          if (!isNaN(numValue)) sum += numValue;
        }
        
        // Set the total value
        if (isCumulativeColumn && allDashes) {
          totalRow.getCell(colIndex).value = 'NA';
        } else {
          totalRow.getCell(colIndex).value = sum;
        }
      }
      
      // ✅ Apply left-alignment AFTER populating all values
      totalRow.eachCell((cell) => {
        cell.alignment = { horizontal: "left", vertical: "middle" };
      });
    }

    // ✅ For 'state' report, TOTAL row already exists in transformedData

    // Fixed column width: 1.70 cm ≈ 9 character units
    columns.forEach((col, colIndex) => {
      worksheet.getColumn(colIndex + 1).width = 9;
    });

    // Generate and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // Filename based on report type
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const timeStr = now.toISOString().split('T')[1].split('.')[0].replace(/:/g, '');
    
    const filenameMap = {
      'dist_date': `MIS_Dist_Datewise_Cold_Wave_Report_${dateStr}_${timeStr}.xlsx`,
      'dist': `MIS_Dist_Cold_Wave_Report_${dateStr}_${timeStr}.xlsx`,
      'state': `MIS_State_Cold_Wave_Report_${dateStr}_${timeStr}.xlsx`,
      'state_date': `MIS_State_Datewise_Cold_Wave_Report_${dateStr}_${timeStr}.xlsx`
    };

    const finalFilename = filenameMap[type] || `${filename}_${dateStr}_${timeStr}.xlsx`;
    
    saveAs(blob, finalFilename);
    console.log(`✅ MIS Excel file generated: ${finalFilename}`);
  } catch (error) {
    console.error("❌ MIS Excel generation error:", error);
    alert(`Excel generation failed: ${error.message}`);
    throw error;
  }
};
/**
 * ✅ NEW: Heat Wave MIS Report Excel Generator - Handles all 4 types
 */
export const generateHeatWaveMISExcelReport = async (reportData, reportType) => {
  const { data, fromDate, toDate, filename, selectedView, displayData } = reportData;
  const type = reportType || selectedView || 'dist_date';

  console.log("🔍 Heat Wave MIS Report Excel Generation:", { type, fromDate, toDate });
  console.log("📦 Report Data Structure:", { hasData: !!data, hasDisplayData: !!displayData });

  if (!data || typeof data !== 'object') {
    alert("No data available to export.");
    return;
  }

  try {
    // Import heat wave transformers
    const {
      transformHeatWaveMISDataForExcel,
      transformHeatWaveMISDistrictData,
      transformHeatWaveMISStateData,
      transformHeatWaveMISStateDatewiseData
    } = await import('./ExcelUtils');

    let processedData = data;

    // Transform data based on report type
    let transformedResult;
    let reportName;
    
    switch (type) {
      case 'dist_date':
        transformedResult = transformHeatWaveMISDataForExcel(processedData);
        reportName = 'DIST_DATE_HEAT_WAVE_REPORT';
        break;
      case 'dist':
        transformedResult = transformHeatWaveMISDistrictData(processedData);
        reportName = 'Dist_Heat_Wave_Report';
        break;
      case 'state':
        transformedResult = transformHeatWaveMISStateData(processedData);
        reportName = 'STATE_HEAT_WAVE_REPORT';
        break;
      case 'state_date':
        transformedResult = transformHeatWaveMISStateDatewiseData(processedData);
        reportName = 'STATE_DATE_HEAT_WAVE_REPORT';
        break;
      default:
        transformedResult = transformHeatWaveMISDataForExcel(processedData);
        reportName = 'HEAT_WAVE_REPORT';
    }

    const { columns, transformedData } = transformedResult;

    if (transformedData.length === 0) {
      alert("No data available to export.");
      return;
    }

    console.log(`✅ Transformed ${transformedData.length} rows`);

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sheet 1");

    // Row 1-3: Metadata - Bold, Merged, Left-aligned
    const metadataRow1 = worksheet.addRow([`Report Name: ${reportName}`]);
    metadataRow1.getCell(1).font = { bold: true };
    metadataRow1.getCell(1).alignment = { horizontal: "left", vertical: "middle" };
    worksheet.mergeCells(`A1:${String.fromCharCode(64 + columns.length)}1`);

    const metadataRow2 = worksheet.addRow([`From Date: ${fromDate || 'N/A'}`]);
    metadataRow2.getCell(1).font = { bold: true };
    metadataRow2.getCell(1).alignment = { horizontal: "left", vertical: "middle" };
    worksheet.mergeCells(`A2:${String.fromCharCode(64 + columns.length)}2`);

    const metadataRow3 = worksheet.addRow([`To Date: ${toDate || 'N/A'}`]);
    metadataRow3.getCell(1).font = { bold: true };
    metadataRow3.getCell(1).alignment = { horizontal: "left", vertical: "middle" };
    worksheet.mergeCells(`A3:${String.fromCharCode(64 + columns.length)}3`);

    // Row 4: Headers (Bold, left-aligned)
    const headerRow = worksheet.addRow(columns.map((col) => col.label));
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
    });

    // Row 5+: Data rows (left-aligned)
    transformedData.forEach((row) => {
      const rowData = columns.map((col) => {
        const value = row[col.label];
        return value !== undefined && value !== null ? value : "";
      });
      const dataRow = worksheet.addRow(rowData);
      dataRow.eachCell((cell) => {
        cell.alignment = { horizontal: "left", vertical: "middle" };
      });
    });

    // TOTAL row for state_date (others already have TOTAL from transformers)
    if (type === 'state_date') {
      const totalRowIndex = transformedData.length + 5;
      const totalRow = worksheet.getRow(totalRowIndex);
      totalRow.getCell(1).value = 'TOTAL';
      totalRow.getCell(2).value = 'NA';

      const startCol = 3;
      for (let colIndex = startCol; colIndex <= columns.length; colIndex++) {
        const colLabel = columns[colIndex - 1].label;
        const isCumulativeColumn = colLabel.includes("(Cum)");

        let sum = 0;
        let allDashes = true;

        for (let rowIndex = 5; rowIndex < totalRowIndex; rowIndex++) {
          const cellValue = worksheet.getRow(rowIndex).getCell(colIndex).value;
          if (cellValue === "-") {
            continue;
          } else {
            allDashes = false;
          }
          const numValue = parseFloat(cellValue);
          if (!isNaN(numValue)) sum += numValue;
        }

        if (isCumulativeColumn && allDashes) {
          totalRow.getCell(colIndex).value = 'NA';
        } else {
          totalRow.getCell(colIndex).value = sum;
        }
      }

      totalRow.eachCell((cell) => {
        cell.alignment = { horizontal: "left", vertical: "middle" };
      });
    }

    // Fixed column width
    columns.forEach((col, colIndex) => {
      worksheet.getColumn(colIndex + 1).width = 9;
    });

    // Generate and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const timeStr = now.toISOString().split('T')[1].split('.')[0].replace(/:/g, '');

    const filenameMap = {
      'dist_date': `MIS_Dist_Datewise_Heat_Wave_Report_${dateStr}_${timeStr}.xlsx`,
      'dist': `MIS_Dist_Heat_Wave_Report_${dateStr}_${timeStr}.xlsx`,
      'state': `MIS_State_Heat_Wave_Report_${dateStr}_${timeStr}.xlsx`,
      'state_date': `MIS_State_Datewise_Heat_Wave_Report_${dateStr}_${timeStr}.xlsx`
    };

    const finalFilename = filenameMap[type] || `${filename}_${dateStr}_${timeStr}.xlsx`;
    saveAs(blob, finalFilename);
    console.log(`✅ Heat Wave MIS Excel file generated: ${finalFilename}`);

  } catch (error) {
    console.error("❌ Heat Wave MIS Excel generation error:", error);
    alert(`Excel generation failed: ${error.message}`);
    throw error;
  }
};

/**
 * ✅ NEW HELPER FUNCTION: Merge cumulative data with daily records for state report
 * 
 * The backend sends:
 * - data: Array of daily records with "Today" fields
 * - cumulative_data.dr_cold_wave_cum: Array of cumulative records with "_cum" fields
 * 
 * This function merges them by matching district names
 */
function mergeCumulativeDataForState(dailyRecords, cumulativeRecords) {
  console.log("🔀 Merging cumulative data...");
  console.log(`   Daily records: ${Array.isArray(dailyRecords) ? dailyRecords.length : 'N/A'}`);
  console.log(`   Cumulative records: ${Array.isArray(cumulativeRecords) ? cumulativeRecords.length : 'N/A'}`);

  if (!Array.isArray(dailyRecords) || !Array.isArray(cumulativeRecords)) {
    console.warn("⚠️  Invalid data structure for merging");
    return dailyRecords;
  }

  if (cumulativeRecords.length === 0) {
    console.warn("⚠️  No cumulative data to merge");
    return dailyRecords;
  }

  // Create lookup map by district name (case-insensitive)
  const cumMap = {};
  cumulativeRecords.forEach((cumRec) => {
    const distKey = (cumRec.dist_name || cumRec.district_name || "").trim().toUpperCase();
    if (distKey) {
      cumMap[distKey] = cumRec;
      console.log(`   ✓ Cached cumulative data for: ${distKey}`);
    }
  });

  // Merge cumulative fields into daily records
  const mergedData = dailyRecords.map((dailyRec) => {
    const distKey = (dailyRec.dist_name || dailyRec.district_name || "").trim().toUpperCase();
    const cumRec = cumMap[distKey];

    if (cumRec) {
      console.log(`   ✓ Merged cumulative data for district: ${distKey}`);
    } else {
      console.warn(`   ✗ No cumulative data found for: ${distKey}`);
    }

    // Return merged record with all cumulative fields
    return {
      ...dailyRec,
      // Add all cumulative fields from the matching cumulative record
      number_of_bonfire_places_cum: cumRec?.number_of_bonfire_places_cum ?? 0,
      number_of_night_shelters_cum: cumRec?.number_of_night_shelters_cum ?? 0,
      number_of_deaths_cum: cumRec?.number_of_deaths_cum ?? 0,
      number_of_ppl_taking_shelter_in_night_shelters_cum:
        cumRec?.number_of_ppl_taking_shelter_in_night_shelters_cum ?? 0,
      number_of_blankets_distributed_cum: cumRec?.number_of_blankets_distributed_cum ?? 0,
      quantity_of_wood_burnt_till_now: cumRec?.quantity_of_wood_burnt_till_now ?? 0,
    };
  });

  console.log(`✅ Merged ${mergedData.length} records with cumulative data`);
  return mergedData;
}

/**

/**
 * ✅ UPDATED: Router function - Auto-detects cold wave AND heat wave report types
 */
export const generateReportExcel = async (reportData) => {
  const { toolName, selectedView } = reportData;
  console.log("🔀 generateReportExcel called with:", { toolName, selectedView });

  const reportType = selectedView || toolName;

  // Cold Wave MIS report types
  const coldWaveMISTypes = ['dist_date', 'dist', 'state', 'state_date',
    'coldwave_mis_district_datewise', 'coldwave_mis_district',
    'coldwave_mis_state', 'coldwave_mis_state_datewise'];

  // Heat Wave MIS report types
  const heatWaveMISTypes = [
    'heatwave_mis_district_datewise', 'heatwave_mis_district',
    'heatwave_mis_state', 'heatwave_mis_state_datewise'];

  if (coldWaveMISTypes.includes(reportType)) {
    const viewMap = {
      'coldwave_mis_district_datewise': 'dist_date',
      'coldwave_mis_district': 'dist',
      'coldwave_mis_state': 'state',
      'coldwave_mis_state_datewise': 'state_date'
    };
    const type = viewMap[reportType] || reportType;
    console.log(`✅ Routing to Cold Wave MIS Report: ${type}`);
    return await generateMISExcelReport(reportData, type);
  } else if (heatWaveMISTypes.includes(reportType)) {
    const viewMap = {
      'heatwave_mis_district_datewise': 'dist_date',
      'heatwave_mis_district': 'dist',
      'heatwave_mis_state': 'state',
      'heatwave_mis_state_datewise': 'state_date'
    };
    const type = viewMap[reportType] || reportType;
    console.log(`✅ Routing to Heat Wave MIS Report: ${type}`);
    return await generateHeatWaveMISExcelReport(reportData, type);
  } else {
    console.log("✅ Routing to generateExcelReport (daily report)");
    return await generateExcelReport(reportData);
  }
};

